import { useMeRestaurants } from '@bonboca/queries';
import { Menu } from 'lucide-react';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, ScrollRestoration, useLocation, useParams } from 'react-router-dom';

import { useCurrentUser } from '../../lib/auth';
import { Toaster } from '../ui/sonner';

import { ManageSidebar } from './ManageSidebar';

export const LAST_RESTAURANT_KEY = 'bb_manage_last_restaurant';

export function ManageLayout() {
  const { user, loading } = useCurrentUser();
  const location = useLocation();
  const { t } = useTranslation('common');

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        {t('loading')}
      </div>
    );
  }

  if (!user) {
    return <Navigate replace to={`/account/login?next=${encodeURIComponent(location.pathname)}`} />;
  }

  return (
    <>
      <div className="flex h-screen overflow-hidden bg-background">
        <ManageShell />
      </div>
      <Toaster />
    </>
  );
}

function ManageShell() {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const location = useLocation();
  const { t } = useTranslation('manage');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const restaurantsQuery = useMeRestaurants();

  const restaurants = restaurantsQuery.data?.items ?? [];
  const isOnboarding = location.pathname.includes('/manage/onboarding');
  const showSidebar = !isOnboarding;

  useEffect(() => {
    if (restaurantId) {
      localStorage.setItem(LAST_RESTAURANT_KEY, restaurantId);
    }
  }, [restaurantId]);

  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  return (
    <>
      {showSidebar ? (
        <ManageSidebar
          currentRestaurantId={restaurantId}
          mobileOpen={mobileSidebarOpen}
          onMobileClose={() => setMobileSidebarOpen(false)}
          restaurants={restaurants}
        />
      ) : null}
      <main className="flex flex-1 flex-col overflow-y-auto min-w-0">
        {showSidebar && (
          <div className="flex h-14 shrink-0 items-center border-b border-border/60 bg-card px-4 md:hidden">
            <button
              aria-label={t('nav.openMenu')}
              className="rounded-lg p-2 text-foreground hover:bg-muted/50"
              onClick={() => setMobileSidebarOpen(true)}
              type="button"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        )}
        <ScrollRestoration />
        <Suspense fallback={<div className="flex-1 animate-pulse bg-muted/30" />}>
          <Outlet />
        </Suspense>
      </main>
    </>
  );
}
